import classNames from 'classnames';
import { useFlags } from 'launchdarkly-react-client-sdk';
import isArray from 'lodash/isArray';
import isFunction from 'lodash/isFunction';
import { Menubar } from 'primereact/menubar';
import { PanelMenu } from 'primereact/panelmenu';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import AccountVerification from '../accountVerification';
import { HeaderSignIn } from '../authentication/HeaderSignIn.component';
import BillingStatus from '../billingStatus';
import ChatbotModule from '../chatbot';
import Data from '../data';
import { PlanIds } from '../data/account';
import ModalModule from '../modal';
import Payment from '../payment';
import ShipStationModule from '../shipstation';
import UpgradePlanModal from '../subscriptionPlan/UpgradePlanModal.component';
import { showUpgradePlanModal } from '../subscriptionPlan/UpgradePlanPopover';
import Utils from '../utils';
import useConstructor from '../utils/customHooks';
import StyleLoader from '../utils/StyleLoader';
import HeaderDataBuilder from './HeaderDataBuilder';
import { LabsDefaultUIMenuItem } from './LabsDefaultUIMenuItem.component';
import PrintLimitDetails from './PrintLimitDetails.component';
import UpgradeDialog from './UpgradeDialog.component';

const { AddFundsModal, PastDueServiceFeesModal, Util: PaymentUtil } = Payment;
const { AccountVerificationModal } = AccountVerification;
const {
	Session,
	Navigation,
	withLayoutAndModalContexts,
	BillingPlans,
	Analytics,
} = Utils;
const { ModalButtonActionType } = ModalModule;
const { ShipStation } = ShipStationModule;
const { BillingStatusModal } = BillingStatus;
const { launchChatbot } = ChatbotModule;
const { Capability } = Data;

/**
 * Calculate screenType based on LayoutContext
 * @param {LayoutContext}
 * @return {string} 'phone', 'tablet' or 'desktop'
 */
const getScreenType = ({ isMobile, screenLayout, deviceType }) => {
	// when deviceType is either 'mobile' or 'tablet'
	if (isMobile) {
		return deviceType === 'mobile' ? 'phone' : 'tablet';
	}

	return screenLayout;
};

const Header = ({
	signInCallback,
	showSignInView,
	layoutContext,
	plainHeader = false,
	modalContext,
}) => {
	const [left, setLeft] = useState();
	const [right, setRight] = useState();
	const [mouseOnMenuArray, setMouseOnMenuArray] = useState([]);
	const [subscriberId, setSubscriberId] = useState();
	const screenType = getScreenType(layoutContext);
	const appLogo = <div className={`app-logo ${screenType}`} />;
	const chatbot = true;
	const flags = useFlags();
	const { canSeeHomepage = false, showLabs = false } = flags;

	const HeaderPanelMenuToggleButton = ({ active }) => {
		const className = classNames('header-panel-menu-toggle-button', {
			'hmenu-default': !active,
			'hmenu-active': active,
		});

		return <div className={className} role="presentation" />;
	};

	const renderMenuItemTemplate = (item, options) => {
		const hasSubItems = item.items && item.items.length > 0;
		const text2className = classNames('p-menuitem-text', {
			'p-menuitem-highlighttext': item.highlightText === true,
		});

		const isRootOrActive = options.props.root || options.active;
		const isRootAndInactive = options.props.root && !options.active;
		const isNotRootAndActive = !options.props.root && options.active;
		const isNotRootAndInactive = !options.props.root && !options.active;
		// For ADL theme
		const iconItemClassName = classNames('p-submenu-icon pi', {
			'pi-sort-down-fill': isRootAndInactive || isNotRootAndInactive,
			'pi-sort-up-fill': isRootOrActive || isNotRootAndActive,
		});

		const { isRootClickable = false } = item;

		const linkClassNames = classNames(
			'p-menuitem-link',
			{
				'p-menuitem-header': item.isRootMenu === true,
			},
			{ 'p-menuitem-active': options.active }
		);
		let upgradeClassNameForSubMenuList = '';

		// SDC change to render sign-in Form
		if (item.id === 'signInform') {
			return <HeaderSignIn signInCallback={signInCallback} />;
		}

		if (item.isPlanUpgradeNeeded) {
			if (item.id === 'scheduleUspsPickUp') {
				upgradeClassNameForSubMenuList = 'submenulist-upgrade';
			} else {
				upgradeClassNameForSubMenuList = '';
			}
		}

		return (
			<a
				href={item.url || '#'}
				className={
					item.id === 'menutoggle'
						? classNames('header-panel-menu-toggle-link')
						: linkClassNames
				}
				style={item.style}
				target={item.target}
				data-qa={`menuitem-link-${item.id}`}
				{...{ dataqa: `menuitem-link-${item.id}` }}
				onClick={
					item.id === 'labsDefaultUI'
						? e => e.preventDefault()
						: options.onClick
				}
				onMouseEnter={event => {
					if (
						screenType === 'desktop' &&
						!options.active &&
						(item.id === 'menutoggle' ||
							(options.props.root && hasSubItems && !isRootClickable))
					) {
						options.onClick(event);
					}

					const newMenuArray = mouseOnMenuArray;
					newMenuArray.push(item.id);
					setMouseOnMenuArray(newMenuArray);
				}}
				onMouseLeave={event => {
					const newMenuArray = mouseOnMenuArray;
					newMenuArray.splice(newMenuArray.indexOf(item.id));
					setMouseOnMenuArray(newMenuArray);

					setTimeout(function () {
						if (item.id === 'menutoggle' || mouseOnMenuArray.length === 0) {
							if (options.props.root && hasSubItems && !isRootClickable) {
								options.onClick(event);
							} else if (options.props.onLeafClick) {
								options.props.onLeafClick(event);
							}
						}
					}, 500);
				}}
				role="menuitem"
				aria-haspopup={isArray(item.items)}
			>
				{item.id === 'menutoggle' &&
					HeaderPanelMenuToggleButton({
						active: !!(options && options.active),
					})}

				{item.id === 'labsDefaultUI' && <LabsDefaultUIMenuItem />}
				{item.id === 'userName' && (
					<div
						className="user-account-icon"
						aria-label={item.label || 'User Account'}
						role="presentation"
					/>
				)}
				{item.id !== 'balance' &&
					item.id !== 'shipStationUpgrade' &&
					item.id !== 'labsDefaultUI' &&
					item.id !== 'userName' && (
						<span>
							<span className="p-menuitem-text">{item.label}</span>
							{item.isPlanUpgradeNeeded && (
								<div
									className={`feature-gate-icon ${upgradeClassNameForSubMenuList}`}
								/>
							)}
							{item.isPlanUpgradeNeeded && (
								<div
									className={`feature-gate-icon-hover ${upgradeClassNameForSubMenuList}`}
								/>
							)}
						</span>
					)}
				{item.badgeIndicator === true && <span className="badge-indicator" />}
				{item.id === 'balance' && (
					<span className="p-menuitem-text">
						{item.balance.substring(1, 1) === '-' ? (
							<span className="dollar-amount-text-negative">
								{item.balance}
							</span>
						) : (
							<span className="dollar-amount-text-positive">
								{item.balance}
							</span>
						)}
					</span>
				)}
				{item.showBadge === true && (
					<span className="header-badge">{item.badgeCount}</span>
				)}
				{item.id === 'shipStationUpgrade' && (
					<>
						<span className="p-menuitem-text">
							{item.text1}
							<br />
						</span>
						<span className={text2className}>{item.text2}</span>
					</>
				)}
				{hasSubItems && item.id !== 'userName' && (
					<span className={iconItemClassName} />
				)}
			</a>
		);
	};

	/**
	 * "Buy More" click handler.
	 * @param {boolean} isRestricted `true` if it's restricted to the user.
	 */
	const onBuyMoreClick = isRestricted => {
		if (isRestricted) {
			modalContext.showMessage({
				data: {
					title: 'Contact Administrator',
					description: 'Please contact your administrator to add funds.',
					primaryButtonType: ModalButtonActionType.Ok,
					primaryButtonText: 'Close',
				},
			});
		} else if (Session.showBillingStatusModal()) {
			modalContext.showModal({
				component: BillingStatusModal,
			});
		} else if (PaymentUtil.isInResubmitState()) {
			modalContext.showModal({
				component: PastDueServiceFeesModal,
			});
		} else if (Session.isAccountVerificationRequired()) {
			modalContext.showModal({
				component: AccountVerificationModal,
				data: {
					completionCallback: success => {
						if (!success) {
							modalContext.showError({
								data: {
									description:
										'To change your phone number, you must verify your updated phone number.',
								},
							});
						}
					},
				},
			});
		} else {
			modalContext.showModal({
				component: AddFundsModal,
				data: {
					// reload account info which then update header
					onPurchaseSuccessful: () => Session.loadBalance(),
				},
			});
		}
	};

	const handleNavigation = ({ item }) => {
		const additionalInfo = {};
		const storeQueryLookup = {
			supplies: 'supplies+tab',
			netstamps: 'netstamps',
			labels: 'labels',
			shippingSupplies: 'shipping+supplies',
			envelopes: 'mailing+supplies',
			freeUspsSupplies: 'free+usps+supplies',
		};

		if (item.id === 'labsDefaultUI') {
			// Labs menu item is a custom component, no navigation needed
			return;
		} else if (item.isRestricted === true) {
			modalContext.showModal({
				component: UpgradeDialog,
				data: {
					upgradeType: item.id,
					handleChangePlan: () =>
						Navigation.navigateTo('storeChangePlan', additionalInfo),
				},
			});
			return;
		} else if (item.isPlanUpgradeNeeded === true) {
			// Reports uses the shared Professional-plan modal (same as cost
			// codes), no custom trigger feature / upgrade prompt.
			if (item.id === 'reports') {
				showUpgradePlanModal(modalContext.showModal);
				return;
			}

			let upgradePrompt;
			let upgradePlan;
			let triggerFeature;
			if (item.id === 'orders') {
				upgradePrompt = 'upgradePromptOrdersTab';
				upgradePlan = PlanIds.EcommerceV383;
				triggerFeature = 'Orders';
			} else if (item.id === 'products') {
				upgradePrompt = 'upgradePromptProductsTab';
				upgradePlan = PlanIds.EcommerceV383;
				triggerFeature = 'Products';
			} else if (item.id === 'scheduleUspsPickUp') {
				upgradePrompt = 'upgradePromptSchedulePickup';
				upgradePlan = PlanIds.CoreV380;
			}
			const segmentTracking = userAction => {
				const { customerDetails = {} } = Session.getAccountInfo();
				const customerEmail = customerDetails?.email;

				// EVENT USED BY MARKETING TEAM
				Analytics.trackEvent(
					upgradePrompt,
					{
						pointOfOrigin: 'headerNavigation',
						userAction,
						email: customerEmail,
					},
					Analytics.agent.segment
				);
			};

			modalContext.showModal({
				component: UpgradePlanModal,
				data: {
					upgradePlan: BillingPlans.getPlanByPlanId(upgradePlan),
					upgradePrompt,
					triggerFeature,
					onUpgradePlanCallback: () => {
						const canNavigateToOrders =
							Session.hasCapability(Capability.CanUseOrders) &&
							item.id === 'orders';
						const canNavigateToProducts =
							Session.hasCapability(Capability.CanUseProducts) &&
							item.id === 'products';
						const canNavigateToPickup =
							Session.hasCapability(Capability.CanUseSchedulePickup) &&
							item.id === 'scheduleUspsPickUp';

						if (
							canNavigateToOrders ||
							canNavigateToProducts ||
							canNavigateToPickup
						) {
							handleNavigation({ item });
						}
					},
					segmentTrackingCallback: segmentTracking,
				},
				closeCallback: () => {
					segmentTracking('closeModal');
				},
			});
			return;
		} else if (item.navType) {
			additionalInfo.navType = item.navType;
		} else if (item.id === 'signOut') {
			return Session.triggerTimeout();
		} else if (item.id === 'shipStationUpgrade') {
			return ShipStation.goToUpgrade();
		} else if (item.id === 'helpChatbot') {
			launchChatbot({ flags, showModal: modalContext.showModal });
			return;
		}

		if (
			item.id.search(
				/netstamps|labels|shippingSupplies|envelopes|freeUspsSupplies|supplies/
			) !== -1
		) {
			additionalInfo.queryString = `utm_source=webclient&utm_medium=top+nav&utm_campaign=${
				storeQueryLookup[item.id]
			}`;
		}

		Navigation.navigateTo(item.id, additionalInfo);
	};

	// render a warning modal if the user's form state is dirty - otherwise navigate to menu item
	const onItemClick = item => {
		if (
			item.isRootMenu &&
			item.items &&
			item.items.length > 0 &&
			item.isRootClickable !== true
		) {
			// set the active class for the selected menu item
			if (
				item.id === 'userName' ||
				item.id === 'balance' ||
				item.id === 'supplies' ||
				item.id === 'help'
			) {
				const menuItems = document.querySelectorAll(
					'#userName, #balance, #supplies, #help'
				);
				for (let i = 0; i < menuItems.length; i++) {
					menuItems[i].classList.remove('p-menuitem-active-parent');
				}
				document
					.getElementById(item.id)
					.classList.add('p-menuitem-active-parent');
			}
			return;
		}

		if (Session.isFormStateDirty) {
			return modalContext.showDirtyStateModal({
				data: {
					callback: handleNavigation,
					item,
				},
			});
		}

		if (item.id === 'signIn' && isFunction(showSignInView)) {
			showSignInView();
		} else if (item.id === 'addFunds') {
			onBuyMoreClick(item.isRestricted);
		} else {
			handleNavigation({ item });
		}
	};

	const processSubItems = subMenuItem => {
		// Don't override command if it already exists (e.g., labsDefaultUI has its own)
		if (!subMenuItem.command) {
			subMenuItem.command = event => {
				onItemClick(event.item);
			};
		}
		subMenuItem.template = (item, options) =>
			renderMenuItemTemplate(item, options);
		subMenuItem.className =
			subMenuItem.id === 'shipStationUpgrade'
				? 'shipstation-upgrade'
				: subMenuItem.className;
		subMenuItem.className =
			subMenuItem.id === 'userName'
				? 'user-name-menu-item'
				: subMenuItem.className;

		if (subMenuItem.items && subMenuItem.items.length > 0) {
			subMenuItem.items = subMenuItem.items
				.filter(subItem => subItem.visible !== false)
				.map(subItem => processSubItems(subItem));
		}

		return subMenuItem;
	};

	const processLeftItems = items => {
		let { Page = '' } = Session.getSettings();

		// Since we're using the mail app for the home page,
		// we need to set the Page to home.
		if (Page === 'mail') {
			Page = 'home';
		}

		return items.map(leftItem => {
			const isItemActive = Page.toLowerCase() === leftItem.id;

			leftItem.template = (item, options) =>
				renderMenuItemTemplate(item, options);
			leftItem.command =
				!(leftItem.isRootMenu && leftItem.items && leftItem.items.length > 0) ||
				leftItem.isRootClickable
					? event => {
							onItemClick(event.item);
						}
					: null;

			leftItem.className = isItemActive
				? 'p-menuitem-active-parent'
				: leftItem.className;

			leftItem.className = leftItem.disabled
				? 'p-disabled'
				: leftItem.className;

			if (leftItem.items && leftItem.items.length > 0) {
				leftItem.items = leftItem.items.filter(
					subItem => subItem.visible !== false
				);

				leftItem.items = leftItem.items.map(subItem =>
					processSubItems(subItem)
				);
			}
			return leftItem;
		});
	};

	const processRightItems = items =>
		items.map(rightItem => {
			rightItem.template = (item, options) =>
				renderMenuItemTemplate(item, options);
			rightItem.command = !(
				rightItem.isRootMenu &&
				rightItem.items &&
				rightItem.items.length > 0
			)
				? event => {
						onItemClick(event.item);
					}
				: null;

			rightItem.className = rightItem.disabled
				? 'p-disabled'
				: rightItem.className;

			rightItem.className =
				rightItem.id === 'balance'
					? 'user-balance-menu-item'
					: rightItem.className;

			if (rightItem.items && rightItem.items.length > 0) {
				rightItem.items = rightItem.items.filter(
					subItem => subItem.visible !== false
				);

				rightItem.items = rightItem.items.map(subItem =>
					processSubItems(subItem)
				);
			}

			return rightItem;
		});

	const updateHeader = async () => {
		const { planDetails = {} } = Session.getAccountInfo();
		const { amountAvailable } = Session.getBalance();
		const { isPayAsYouGo } = planDetails;

		// Get LaunchDarkly feature flag for Labs section
		const showLabsFlag = showLabs === true;

		const { left: newLeft, right: newRight } = HeaderDataBuilder.getConfig({
			screenType: getScreenType(layoutContext),
			showChatWithUs: chatbot,
			canSeeHomepage,
			showLabs: showLabsFlag,
		});

		if (left !== newLeft) {
			setLeft(processLeftItems(newLeft));
		}

		if (right !== newRight) {
			isPayAsYouGo && amountAvailable === 0
				? setRight(
						processRightItems(newRight.filter(item => item.id !== 'balance'))
					)
				: setRight(processRightItems(newRight));
		}
	};

	useConstructor(() => {
		const id = Session.subscribe(() => {
			updateHeader();
		});

		setSubscriberId(id);
	});

	useEffect(() => {
		updateHeader();

		return () => Session.unsubscribe(subscriberId);
	}, [layoutContext.screenLayout]);

	const onHeaderLogoClick = () => {
		// per WEBAPPS-25889, clicking the logo should show Sign-In view.
		Session.triggerTimeout();
	};

	const HeaderMenuBar = ({ className, id, model, style, start }) => {
		const cls = classNames('p-menubar p-component', className);

		return (
			<div id={id} className={cls} style={style}>
				<Menubar model={model} start={start} multiple={false} />
			</div>
		);
	};

	const HeaderPanelMenu = ({ model, className, id, style }) => {
		const classNamesStr = classNames(className);

		return (
			<div id={id} className={classNamesStr} style={style}>
				<PanelMenu className="p-panelmenu-root-submenu" model={model} />
			</div>
		);
	};

	const renderHeaderDesktop = () => {
		const {
			planDetails: { isPayAsYouGo, monthlyPrintLimit },
			nextBillingDate,
			balancePrintCount,
		} = Session.getAccountInfo();
		return (
			<div className="header-panel-desktop">
				<HeaderMenuBar
					className="header-menu-left"
					model={left}
					start={appLogo}
				/>
				<div className="header-menu-center">
					{isPayAsYouGo && (
						<PrintLimitDetails
							monthlyPrintLimit={monthlyPrintLimit}
							balancePrintCount={balancePrintCount}
							nextBillingDate={nextBillingDate}
						/>
					)}
				</div>
				<HeaderMenuBar
					className="header-menu-right"
					model={right}
					signInCallback={signInCallback}
				/>
			</div>
		);
	};

	const renderHeaderTablet = () => (
		<div className="header-panel-tablet">
			<div className="header-panel-tablet-left">
				<HeaderMenuBar
					className="header-menu-left"
					model={left}
					start={appLogo}
				/>
			</div>
			<div className="header-panel-tablet-right">
				<HeaderPanelMenu className="tablet-panel-menu" model={right} />
			</div>
		</div>
	);

	const renderHeaderMobile = () => (
		<>
			{appLogo}
			<div className="header-panel-phone">
				<HeaderPanelMenu model={left} />
			</div>
			<HeaderMenuBar className="header-menu-right-phone" model={right} />
		</>
	);

	const renderPlainHeader = () => (
		<div
			className={`app-logo ${screenType}`}
			onClick={onHeaderLogoClick}
			role="button"
			tabIndex={0}
			label="plain header"
		/>
	);

	return (
		<div className="common-header">
			{plainHeader ? (
				renderPlainHeader()
			) : (
				<>
					{screenType === 'desktop' && renderHeaderDesktop()}
					{screenType === 'tablet' && renderHeaderTablet()}
					{screenType === 'phone' && renderHeaderMobile()}
				</>
			)}
		</div>
	);
};

export default withLayoutAndModalContexts(Header);

Header.prototype = {
	// only render a plain header with logo only
	plainHeader: PropTypes.bool,
};
